
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

export interface TranslatedContent {
  translatedQuestion: string;
  translatedOptions: string[];
}

type AIProcessingType = 'summarize' | 'translate' | 'chat' | 'doubt' | 'learning' | 'mindmap' | 'lecture';

interface ProcessingOptions {
  targetLanguage?: string;
}

export class AIHybridService {
  private static async callOpenAIFunction(
    prompt: string, 
    systemPrompt: string,
    maxTokens: number = 1000
  ): Promise<string> {
    try {
      const { data, error } = await supabase.functions.invoke('ai-hybrid', {
        body: {
          prompt,
          systemPrompt,
          maxTokens
        }
      });

      if (error) throw error;
      return data?.content || '';
    } catch (error) {
      console.error("Error calling OpenAI function:", error);
      throw new Error("Failed to process your request with AI service");
    }
  }

  public static async processText(
    text: string, 
    type: AIProcessingType,
    options: ProcessingOptions = {}
  ): Promise<string> {
    let systemPrompt = '';
    let prompt = '';
    let maxTokens = 1000;

    // Special handling for lecture generation using Claude
    if (type === 'lecture') {
      try {
        const { data, error } = await supabase.functions.invoke('ai-claude', {
          body: {
            message: `Create a comprehensive lecture on ${text} with:
1. Introduction and context
2. Key concepts and definitions (with mathematical notation where relevant)
3. Detailed explanations with examples
4. Common applications
5. Practice problems (if applicable)
\n\nMake sure to properly format all mathematical expressions using LaTeX notation and use proper markdown formatting.`,
            type: 'lecture'
          }
        });

        if (error) throw error;
        return data?.response || '';
      } catch (error) {
        console.error('Error in lecture generation:', error);
        toast.error('Failed to generate lecture content');
        throw error;
      }
    }

    switch (type) {
      case 'summarize':
        systemPrompt = "You are a professional summarizer. Create a concise, well-structured summary of the provided text.";
        prompt = `Summarize the following text in a clear, structured format with bullet points for key takeaways:\n\n${text}`;
        maxTokens = 500;
        break;

      case 'translate':
        if (!options.targetLanguage) {
          throw new Error("Target language is required for translation");
        }
        
        systemPrompt = "You are a professional translator. Translate the text accurately while preserving meaning and tone.";
        
        // Special handling for Hinglish
        if (options.targetLanguage === 'hinglish') {
          systemPrompt = "You are a professional translator specializing in Hinglish (Hindi words written in English script mixed with English). Translate the text to a natural Hinglish that an Indian English speaker would use in casual conversation.";
          prompt = `Translate this text to Hinglish (mix of Hindi and English that uses Roman script):\n\n${text}`;
        } else {
          prompt = `Translate this text to ${options.targetLanguage}:\n\n${text}`;
        }
        break;

      case 'chat':
        systemPrompt = "You are an educational AI assistant. Provide helpful, informative responses to student questions.";
        prompt = text;
        maxTokens = 1500;
        break;

      case 'doubt':
        systemPrompt = "You are an educational tutor specializing in solving academic doubts. Provide clear, step-by-step explanations.";
        prompt = `Help the student understand the following doubt/question in detail:\n\n${text}`;
        maxTokens = 1500;
        break;

      case 'learning':
        // We'll now use the generate-learning-content edge function instead
        try {
          const { data, error } = await supabase.functions.invoke('generate-learning-content', {
            body: {
              topic: text,
              contentType: options.targetLanguage || 'lecture'
            }
          });
          
          if (error) throw error;
          return data.content || '';
        } catch (error) {
          console.error('Error generating learning content:', error);
          toast.error('Failed to generate learning content');
          throw error;
        }

      case 'mindmap':
        systemPrompt = "You are an expert at creating educational mind maps. Structure information hierarchically with main concepts and related subconcepts.";
        prompt = `Create a detailed mind map for the topic: ${text}. Format it as a hierarchical structure with markdown headings and bullet points. Start with the main concept, then use ## for major branches and ### for subbranches, using bullet points (*) for details under each branch.`;
        maxTokens = 2000;
        break;

      default:
        throw new Error(`Unsupported processing type: ${type}`);
    }

    try {
      console.log(`Processing ${type} request with AI service`);
      return await this.callOpenAIFunction(prompt, systemPrompt, maxTokens);
    } catch (error) {
      console.error(`Error in ${type} processing:`, error);
      toast.error(`AI processing error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error;
    }
  }
  
  public static async translateQuizContent(
    question: string, 
    options: string[], 
    targetLanguage: string
  ): Promise<TranslatedContent> {
    try {
      // Create a single text with question and options for efficient translation
      const contentToTranslate = `
Question: ${question}
Options:
${options.map((opt, idx) => `${idx+1}. ${opt}`).join('\n')}
      `;
      
      // Call the AI tutoring function to handle translation
      const { data, error } = await supabase.functions.invoke('ai-tutoring', {
        body: {
          action: 'translate',
          data: {
            text: contentToTranslate,
            targetLanguage
          }
        }
      });

      if (error) {
        console.error("Translation API error:", error);
        throw new Error(`Translation API error: ${error.message}`);
      }
      
      if (!data || !data.translation) {
        throw new Error("No translation data returned");
      }
      
      const translatedContent = data.translation;
      
      // Parse the translated content
      const lines = translatedContent.split('\n');
      let translatedQuestion = '';
      const translatedOptions: string[] = [];
      
      // Extract translated question and options
      let foundQuestion = false;
      let foundOptions = false;
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!foundQuestion && (line.startsWith('Question:') || line.startsWith('प्रश्न:') || line.includes('?'))) {
          translatedQuestion = line.replace(/^(Question:|प्रश्न:)\s*/, '').trim();
          foundQuestion = true;
        } else if (/^\d+\./.test(line) || /^[A-Za-z][\)\.]/.test(line)) {
          // This is an option, extract the text after the number and period
          const optionText = line.replace(/^[\dA-Za-z][\)\.]?\s*/, '').trim();
          if (optionText) {
            translatedOptions.push(optionText);
            foundOptions = true;
          }
        }
      }
      
      // If we couldn't parse the question properly, use a fallback
      if (!foundQuestion) {
        for (const line of lines) {
          if (line && !line.includes('Options:') && !foundQuestion) {
            translatedQuestion = line.trim();
            foundQuestion = true;
            break;
          }
        }
      }
      
      // If we couldn't parse all options, use fallback
      if (!foundOptions || translatedOptions.length !== options.length) {
        console.warn('Translation parsing issue - falling back to original options structure');
        // Try to find all potential option text blocks
        const optionRegex = /[A-D][\.\)]\s*(.*?)(?=(?:[A-D][\.\)]|$))/gs;
        const matches = translatedContent.match(optionRegex);
        
        if (matches && matches.length === options.length) {
          // Extract just the text part from each match
          translatedOptions.splice(0, translatedOptions.length, ...matches.map(
            m => m.replace(/^[A-D][\.\)]\s*/, '').trim()
          ));
        } else {
          // As a last resort, return untranslated options
          console.warn('Could not parse translated options, using originals');
          return {
            translatedQuestion: translatedQuestion || question,
            translatedOptions: options
          };
        }
      }
      
      return { translatedQuestion, translatedOptions };
    } catch (error) {
      console.error('Translation error:', error);
      toast.error('Failed to translate quiz content');
      // Return original content as fallback
      return { translatedQuestion: question, translatedOptions: options };
    }
  }
}
